import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const AdminHODManagement = ({ onNavigate, userInfo = {} }) => {
  // Mobile state (used for responsive content layout)
  const [isMobile, setIsMobile] = useState(false);

  // State for HODs data
  const [hods, setHods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dynamic Department Data
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);

  // Pagination and search state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [limit] = useState(10);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedHOD, setSelectedHOD] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    username: '',
    name: '',
    department: '',
    designation: '',
    email: '',
    phone_number: '',
    batch_name: '',
    blood_group: '',
    pan_number: '',
    adhar_number: '',
    academic_qualification: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Check if mobile on mount and resize (used for responsive content layout)
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // API HELPER FUNCTION (Same as AdminStudentManagement)
  const apiCall = async (endpoint, options = {}) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');

    if (!token) {
      throw new Error('Authentication token not found');
    }

    const response = await fetch(`${API_LINK}/api/admin${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        throw new Error('Session expired');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  };

  // FETCH DEPARTMENTS FROM DATABASE
  const fetchDepartments = async () => {
    try {
      setDepartmentsLoading(true);
      const result = await apiCall('/departments');

      if (result.success) {
        setDepartments(result.data);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      // We don't block the UI for this error, just log it
    } finally {
      setDepartmentsLoading(false);
    }
  };

  // Fetch Departments on Mount
  useEffect(() => {
    fetchDepartments();
  }, []);

  // Fetch HODs from API - FIXED VERSION
  const fetchHODs = async (page = 1, search = '') => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found. Please login again.');
        setLoading(false);
        return;
      }

      // Build parameters object
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });

      // Only add search parameter if it has a value
      if (search && search.trim()) {
        params.append('search', search.trim());
      }

      const apiUrl = `${API_LINK}/api/admin/hods?${params}`;

      console.log('Fetching HODs with URL:', apiUrl);
      console.log('Search term being sent:', search);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();

        if (response.status === 401) {
          setError('Session expired. Please login again.');
          localStorage.removeItem('token');
          sessionStorage.removeItem('token');
          return;
        } else if (response.status === 404) {
          setError('Admin API endpoint not found. Please check if the backend server is running correctly.');
          return;
        } else if (response.status === 500) {
          setError('Server error occurred. Please check the backend logs.');
          return;
        }

        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        setError('Server returned invalid response format. Expected JSON.');
        return;
      }

      const result = await response.json();
      console.log('API Response:', result);

      if (result.success) {
        const hodsData = result.data?.hods || [];
        const paginationData = result.data?.pagination || {};

        console.log('HODs data received:', hodsData.length, 'items');
        console.log('Pagination data:', paginationData);

        setHods(hodsData);
        setCurrentPage(paginationData.currentPage || page);
        setTotalPages(paginationData.totalPages || Math.ceil(hodsData.length / limit));
        setTotalCount(paginationData.totalCount || hodsData.length);
      } else {
        setError(result.message || 'Failed to fetch HODs from server');
      }
    } catch (error) {
      console.error('Fetch HODs error:', error);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setError('Cannot connect to backend server. Please ensure:\n1. Backend server is running \n2. Admin API routes are properly configured\n3. Database connection is working');
      } else if (error.message.includes('JSON')) {
        setError('Server returned invalid data format. Please check backend API.');
      } else {
        setError(`Failed to load HODs: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchHODs(1, '');
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Handle search - FIXED VERSION
  const handleSearch = () => {
    console.log('Search triggered with term:', searchTerm);
    setCurrentPage(1);
    fetchHODs(1, searchTerm);
  };

  // Handle clear search - NEW FUNCTION
  const handleClearSearch = () => {
    console.log('Clearing search');
    setSearchTerm('');
    setCurrentPage(1);
    fetchHODs(1, '');
  };

  // Handle pagination
  const handlePageChange = (page) => {
    if (page && page !== currentPage && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      fetchHODs(page, searchTerm);
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.username.trim()) errors.username = 'Username is required';
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.department.trim()) errors.department = 'Department is required';
    if (!formData.designation.trim()) errors.designation = 'Designation is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    if (!formData.phone_number.trim()) errors.phone_number = 'Phone number is required';
    if (!formData.blood_group.trim()) errors.blood_group = 'Blood group is required';
    if (!formData.pan_number.trim()) errors.pan_number = 'PAN number is required';
    if (!formData.adhar_number.trim()) errors.adhar_number = 'Aadhaar number is required';
    if (!formData.academic_qualification.trim()) errors.academic_qualification = 'Academic qualification is required';

    // Email validation
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email format is invalid';
    }

    // Phone validation
    if (formData.phone_number && !/^\d{10}$/.test(formData.phone_number)) {
      errors.phone_number = 'Phone number must be 10 digits';
    }

    // Aadhaar validation
    if (formData.adhar_number && !/^\d{12}$/.test(formData.adhar_number)) {
      errors.adhar_number = 'Aadhaar number must be 12 digits';
    }

    // PAN validation
    if (formData.pan_number && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan_number)) {
      errors.pan_number = 'PAN format should be like ABCDE1234F';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission for add/edit
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const url = selectedHOD
        ? `${API_LINK}/api/admin/hods/${selectedHOD.id}`
        : `${API_LINK}/api/admin/hods`;

      const method = selectedHOD ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (result.success) {
        setShowAddModal(false);
        setShowEditModal(false);
        resetForm();
        fetchHODs(currentPage || 1, searchTerm);
        alert(selectedHOD ? 'HOD updated successfully!' : 'HOD added successfully!');
      } else {
        alert(result.message || 'Operation failed');
      }
    } catch (error) {
      alert('Failed to save HOD. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!selectedHOD) return;

    try {
      setSubmitting(true);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${API_LINK}/api/admin/hods/${selectedHOD.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.success) {
        setShowDeleteModal(false);
        setSelectedHOD(null);
        fetchHODs(currentPage || 1, searchTerm);
        alert('HOD deleted successfully!');
      } else {
        alert(result.message || 'Delete failed');
      }
    } catch (error) {
      alert('Failed to delete HOD. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      username: '',
      name: '',
      department: '',
      designation: '',
      email: '',
      phone_number: '',
      batch_name: '',
      blood_group: '',
      pan_number: '',
      adhar_number: '',
      academic_qualification: ''
    });
    setFormErrors({});
    setSelectedHOD(null);
  };

  // Handle edit click
  const handleEditClick = (hod) => {
    setSelectedHOD(hod);
    setFormData({
      username: hod.username || '',
      name: hod.name || '',
      department: hod.department || '',
      designation: hod.designation || '',
      email: hod.email || '',
      phone_number: hod.phone_number || '',
      batch_name: hod.batch_name || '',
      blood_group: hod.blood_group || '',
      pan_number: hod.pan_number || '',
      adhar_number: hod.adhar_number?.toString() || '',
      academic_qualification: hod.academic_qualification || ''
    });
    setShowEditModal(true);
  };

  // Handle delete click
  const handleDeleteClick = (hod) => {
    setSelectedHOD(hod);
    setShowDeleteModal(true);
  };



  return (
    <Layout role="admin" onNavigate={onNavigate} activePage="admin-hods" userInfo={userInfo}>
      {/* Welcome Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
        color: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem' }}>
          HOD Management Portal 👤
        </h2>
        <p style={{ margin: 0, opacity: 0.9 }}>
          Manage all Head of Department records, add new HODs, and maintain comprehensive HOD information database.
        </p>
      </div>

      {/* Search and Controls Section - UPDATED */}
      <div style={{
        background: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: '1rem',
          marginBottom: '1rem'
        }}>
          {/* Search Bar - UPDATED */}
          <div style={{ flex: 1, maxWidth: isMobile ? '100%' : '400px' }}>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Search by name, username, department, or email..."
                value={searchTerm}
                onChange={(e) => {
                  console.log('Search term changed:', e.target.value);
                  setSearchTerm(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
                style={{
                  width: '100%',
                  padding: '1rem 3rem 1rem 1rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'all 0.3s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#238548';
                  e.target.style.boxShadow = '0 0 0 3px rgba(35, 133, 72, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.boxShadow = 'none';
                }}
              />

              {/* Search button */}
              <button
                onClick={handleSearch}
                style={{
                  position: 'absolute',
                  right: searchTerm ? '40px' : '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f1f5f9';
                  e.target.style.color = '#238548';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#666';
                }}
                title="Search HODs"
              >
                🔍
              </button>

              {/* Clear search button - NEW */}
              {searchTerm && (
                <button
                  onClick={handleClearSearch}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#fee2e2';
                    e.target.style.color = '#dc2626';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#666';
                  }}
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Add HOD Button */}
          <button
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            style={{
              padding: '1rem 2rem',
              background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 25px rgba(35, 133, 72, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            <span style={{ fontSize: '1.2rem' }}>+</span>
            Add New HOD
          </button>
        </div>

        {/* Stats - UPDATED */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: '1rem',
          borderTop: '1px solid #ecf0f1',
          fontSize: '0.9rem',
          color: '#666'
        }}>
          <span>
            {loading ? 'Loading HODs...' :
              error ? 'Error loading HODs' :
                `Showing ${hods.length} of ${totalCount} HODs`}
            {searchTerm && !loading && !error && (
              <span style={{ color: '#238548', fontWeight: '500' }}>
                {` (filtered by "${searchTerm}")`}
              </span>
            )}
            {searchTerm && !loading && !error && hods.length === 0 && (
              <span style={{ color: '#dc2626', marginLeft: '0.5rem' }}>
                - No matches found
              </span>
            )}
          </span>
          {loading && (
            <span style={{ color: '#238548' }}>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #e3f2fd',
                borderTop: '2px solid #238548',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                display: 'inline-block'
              }}></div>
            </span>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          background: '#fee2e2',
          border: '1px solid #fecaca',
          color: '#dc2626',
          padding: '1rem',
          borderRadius: '12px',
          marginBottom: '2rem'
        }}>
          <p style={{ margin: '0 0 0.5rem 0', fontWeight: '500' }}>⚠️ Error Loading HODs</p>
          <p style={{ margin: '0 0 1rem 0' }}>{error}</p>
          <button
            onClick={() => fetchHODs(currentPage, searchTerm)}
            style={{
              background: '#dc2626',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Try Again
          </button>
        </div>
      )}

      {/* HODs Table */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {loading && hods.length === 0 ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '4rem',
            flexDirection: 'column',
            gap: '1rem'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: '4px solid #e3f2fd',
              borderTop: '4px solid #238548',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading HODs...</p>
          </div>
        ) : hods.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: 0.5 }}>👤</div>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: 600,
              color: '#333',
              margin: '0 0 1rem 0'
            }}>
              {searchTerm ? 'No HODs found' : 'No HODs registered yet'}
            </h3>
            <p style={{ color: '#666', margin: '0 0 2rem 0', fontSize: '1rem' }}>
              {searchTerm
                ? `No HODs match "${searchTerm}". Try adjusting your search criteria.`
                : 'Get started by adding your first HOD to the system'
              }
            </p>
            {!searchTerm && (
              <button
                onClick={() => {
                  resetForm();
                  setShowAddModal(true);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              >
                Add First HOD
              </button>
            )}
            {searchTerm && (
              <button
                onClick={handleClearSearch}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: '2px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  marginLeft: '1rem'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#e5e7eb';
                  e.target.style.borderColor = '#9ca3af';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#f3f4f6';
                  e.target.style.borderColor = '#d1d5db';
                }}
              >
                Clear Search
              </button>
            )}
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: isMobile ? '0.85rem' : '0.95rem'
              }}>
                <thead>
                  <tr style={{
                    background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                    color: '#ffffff'
                  }}>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Name</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Department</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Designation</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Email</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Phone</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'center',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {hods.map((hod, index) => (
                    <tr key={hod.id} style={{
                      borderBottom: '1px solid #f1f5f9',
                      transition: 'background-color 0.3s ease'
                    }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <td style={{ padding: '1rem' }}>
                        <div>
                          <div style={{ fontWeight: 500, color: '#333', marginBottom: '0.25rem' }}>
                            {hod.name}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#666' }}>
                            @{hod.username}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '1rem', color: '#333' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          backgroundColor: '#e8f5e9',
                          color: '#2e7d32',
                          borderRadius: '20px',
                          fontSize: '0.8rem',
                          fontWeight: 500,
                          textTransform: 'uppercase'
                        }}>
                          {hod.department}
                        </span>
                      </td>
                      <td style={{ padding: '1rem', color: '#333' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          backgroundColor: '#fff3e0',
                          color: '#e65100',
                          borderRadius: '20px',
                          fontSize: '0.8rem',
                          fontWeight: 500
                        }}>
                          {hod.designation}
                        </span>
                      </td>
                      <td style={{ padding: '1rem', color: '#333' }}>
                        {hod.email || 'Not provided'}
                      </td>
                      <td style={{ padding: '1rem', color: '#333' }}>
                        {hod.phone_number}
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                          <button
                            onClick={() => handleEditClick(hod)}
                            style={{
                              padding: '0.5rem 1rem',
                              backgroundColor: '#e3f2fd',
                              color: '#1976d2',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.85rem',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              fontWeight: 500
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#1976d2';
                              e.target.style.color = '#ffffff';
                              e.target.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#e3f2fd';
                              e.target.style.color = '#1976d2';
                              e.target.style.transform = 'translateY(0)';
                            }}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => handleDeleteClick(hod)}
                            style={{
                              padding: '0.5rem 1rem',
                              backgroundColor: '#ffebee',
                              color: '#d32f2f',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.85rem',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              fontWeight: 500
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#d32f2f';
                              e.target.style.color = '#ffffff';
                              e.target.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#ffebee';
                              e.target.style.color = '#d32f2f';
                              e.target.style.transform = 'translateY(0)';
                            }}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{
                padding: '1.5rem',
                borderTop: '1px solid #f1f5f9',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <div style={{ color: '#666', fontSize: '0.9rem' }}>
                  Page {currentPage} of {totalPages} • {totalCount} total HODs
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '2px solid #e2e8f0',
                      backgroundColor: currentPage === 1 ? '#f8fafc' : '#ffffff',
                      color: currentPage === 1 ? '#9ca3af' : '#374151',
                      borderRadius: '6px',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      fontSize: '0.9rem'
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.borderColor = '#238548';
                        e.target.style.backgroundColor = '#f0fdf4';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.backgroundColor = '#ffffff';
                      }
                    }}
                  >
                    ← Previous
                  </button>

                  {[...Array(Math.min(5, totalPages))].map((_, index) => {
                    let page;
                    if (totalPages <= 5) {
                      page = index + 1;
                    } else if (currentPage <= 3) {
                      page = index + 1;
                    } else if (currentPage >= totalPages - 2) {
                      page = totalPages - 4 + index;
                    } else {
                      page = currentPage - 2 + index;
                    }

                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        style={{
                          padding: '0.5rem 0.75rem',
                          border: '2px solid',
                          borderColor: page === currentPage ? '#238548' : '#e2e8f0',
                          backgroundColor: page === currentPage ? '#238548' : '#ffffff',
                          color: page === currentPage ? '#ffffff' : '#374151',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          fontSize: '0.9rem',
                          fontWeight: page === currentPage ? '600' : '400'
                        }}
                        onMouseEnter={(e) => {
                          if (page !== currentPage) {
                            e.target.style.borderColor = '#238548';
                            e.target.style.backgroundColor = '#f0fdf4';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (page !== currentPage) {
                            e.target.style.borderColor = '#e2e8f0';
                            e.target.style.backgroundColor = '#ffffff';
                          }
                        }}
                      >
                        {page}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '2px solid #e2e8f0',
                      backgroundColor: currentPage === totalPages ? '#f8fafc' : '#ffffff',
                      color: currentPage === totalPages ? '#9ca3af' : '#374151',
                      borderRadius: '6px',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      fontSize: '0.9rem'
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.borderColor = '#238548';
                        e.target.style.backgroundColor = '#f0fdf4';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.backgroundColor = '#ffffff';
                      }
                    }}
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
            width: '100%',
            maxWidth: '900px',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Modal Header */}
            <div style={{
              background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
              color: '#ffffff',
              padding: '1.5rem 2rem',
              borderRadius: '12px 12px 0 0'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '1.5rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                {selectedHOD ? '✏️ Edit HOD' : '➕ Add New HOD'}
              </h3>
              <p style={{ margin: '0.5rem 0 0 0', opacity: 0.9, fontSize: '0.95rem' }}>
                {selectedHOD ? 'Update HOD information' : 'Enter new HOD details'}
              </p>
            </div>

            {/* Modal Content */}
            <div style={{
              padding: '2rem',
              overflowY: 'auto',
              flex: 1
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1.5rem' }}>
                {/* Username */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Username *
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="Enter username"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.username ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.username ? '#fef2f2' : '#ffffff'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.username) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.username) e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                  {formErrors.username && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.username}
                    </p>
                  )}
                </div>

                {/* Name */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter HOD's full name"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.name ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.name ? '#fef2f2' : '#ffffff'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.name) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.name) e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                  {formErrors.name && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.name}
                    </p>
                  )}
                </div>

                {/* Department - UPDATED WITH ALL DEPARTMENTS */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Department *
                  </label>
                  <select
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    disabled={departmentsLoading}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.department ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: departmentsLoading ? '#f9fafb' : (formErrors.department ? '#fef2f2' : '#ffffff'),
                      cursor: departmentsLoading ? 'not-allowed' : 'pointer'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.department) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.department) e.target.style.borderColor = '#e5e7eb';
                    }}
                  >
                    <option value="">
                      {departmentsLoading ? 'Loading Departments...' : 'Select Department'}
                    </option>
                    {departments.map(dept => (
                      <option key={dept.value} value={dept.value}>
                        {dept.label}
                      </option>
                    ))}
                  </select>
                  {formErrors.department && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.department}
                    </p>
                  )}
                </div>

                {/* Designation */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Designation *
                  </label>
                  <select
                    value={formData.designation}
                    onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.designation ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.designation ? '#fef2f2' : '#ffffff'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.designation) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.designation) e.target.style.borderColor = '#e5e7eb';
                    }}
                  >
                    <option value="">Select Designation</option>
                    <option value="Head of Department">Head of Department</option>
                    <option value="Associate Head">Associate Head</option>
                    <option value="Deputy Head">Deputy Head</option>
                    <option value="Acting Head">Acting Head</option>
                    <option value="Director">Director</option>
                    <option value="Associate Director">Associate Director</option>
                    <option value="Dean">Dean</option>
                    <option value="Associate Dean">Associate Dean</option>
                    <option value="Principal">Principal</option>
                    <option value="Vice Principal">Vice Principal</option>
                  </select>
                  {formErrors.designation && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.designation}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="hod@example.com"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.email ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.email ? '#fef2f2' : '#ffffff'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.email) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.email) e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                  {formErrors.email && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.email}
                    </p>
                  )}
                </div>

                {/* Phone Number */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={formData.phone_number}
                    onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                    placeholder="10-digit phone number"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.phone_number ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.phone_number ? '#fef2f2' : '#ffffff'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.phone_number) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.phone_number) e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                  {formErrors.phone_number && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.phone_number}
                    </p>
                  )}
                </div>

                {/* Batch Name */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Batch Name
                  </label>
                  <input
                    type="text"
                    value={formData.batch_name}
                    onChange={(e) => setFormData({ ...formData, batch_name: e.target.value })}
                    placeholder="e.g., B.Tech-2025"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#238548'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>

                {/* Blood Group */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Blood Group *
                  </label>
                  <select
                    value={formData.blood_group}
                    onChange={(e) => setFormData({ ...formData, blood_group: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.blood_group ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.blood_group ? '#fef2f2' : '#ffffff'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.blood_group) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.blood_group) e.target.style.borderColor = '#e5e7eb';
                    }}
                  >
                    <option value="">Select Blood Group</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                  </select>
                  {formErrors.blood_group && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.blood_group}
                    </p>
                  )}
                </div>

                {/* PAN Number */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    PAN Number *
                  </label>
                  <input
                    type="text"
                    value={formData.pan_number}
                    onChange={(e) => setFormData({ ...formData, pan_number: e.target.value.toUpperCase() })}
                    placeholder="ABCDE1234F"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.pan_number ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.pan_number ? '#fef2f2' : '#ffffff'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.pan_number) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.pan_number) e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                  {formErrors.pan_number && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.pan_number}
                    </p>
                  )}
                </div>

                {/* Aadhaar Number */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Aadhaar Number *
                  </label>
                  <input
                    type="text"
                    value={formData.adhar_number}
                    onChange={(e) => setFormData({ ...formData, adhar_number: e.target.value })}
                    placeholder="12-digit Aadhaar number"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.adhar_number ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.adhar_number ? '#fef2f2' : '#ffffff'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.adhar_number) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.adhar_number) e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                  {formErrors.adhar_number && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.adhar_number}
                    </p>
                  )}
                </div>

                {/* Academic Qualification */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Academic Qualification *
                  </label>
                  <textarea
                    value={formData.academic_qualification}
                    onChange={(e) => setFormData({ ...formData, academic_qualification: e.target.value })}
                    placeholder="e.g., Ph.D. in Computer Science, M.Tech in Software Engineering"
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.academic_qualification ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.academic_qualification ? '#fef2f2' : '#ffffff',
                      resize: 'vertical',
                      minHeight: '80px'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.academic_qualification) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.academic_qualification) e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                  {formErrors.academic_qualification && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.academic_qualification}
                    </p>
                  )}
                </div>

              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '1.5rem 2rem',
              borderTop: '1px solid #f1f5f9',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '1rem',
              backgroundColor: '#f8fafc'
            }}>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                  resetForm();
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '2px solid #e5e7eb',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 500
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.backgroundColor = '#ffffff';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  padding: '0.75rem 2rem',
                  border: 'none',
                  background: submitting
                    ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                    : 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                  color: '#ffffff',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  if (!submitting) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 25px rgba(35, 133, 72, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!submitting) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              >
                {submitting ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #ffffff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Saving...
                  </>
                ) : (
                  <>
                    {selectedHOD ? '💾 Update HOD' : '➕ Add HOD'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedHOD && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
            maxWidth: '400px',
            width: '100%'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '2rem 2rem 1rem 2rem',
              textAlign: 'center'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: '#fee2e2',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                fontSize: '2rem'
              }}>
                🗑️
              </div>
              <h3 style={{
                margin: '0 0 1rem 0',
                fontSize: '1.5rem',
                fontWeight: 600,
                color: '#dc2626'
              }}>
                Delete HOD
              </h3>
              <p style={{
                margin: '0 0 1rem 0',
                color: '#6b7280',
                lineHeight: 1.6
              }}>
                Are you sure you want to delete <strong style={{ color: '#374151' }}>{selectedHOD.name}</strong>?
              </p>
              <p style={{
                margin: 0,
                color: '#dc2626',
                fontSize: '0.9rem',
                fontWeight: 500
              }}>
                This action cannot be undone and will permanently remove all HOD data.
              </p>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '1rem 2rem 2rem 2rem',
              display: 'flex',
              justifyContent: 'center',
              gap: '1rem'
            }}>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedHOD(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '2px solid #e5e7eb',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 500
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.backgroundColor = '#ffffff';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={submitting}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  background: submitting
                    ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                    : 'linear-gradient(135deg, #dc2626 0%, #f87171 100%)',
                  color: '#ffffff',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  if (!submitting) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 25px rgba(220, 38, 38, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!submitting) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              >
                {submitting ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #ffffff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Deleting...
                  </>
                ) : (
                  '🗑️ Delete HOD'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AdminHODManagement;